import {Box, createStyles, MultiSelect, Radio, Select, Stack} from '@mantine/core';
import React from 'react';

import RoutineFieldHeader from '../RoutineFieldHeader';
import {EventTriggerConfig} from '../RoutineTypes';
import {withIndefiniteArticle} from '../RoutineUtils';
import {FIELD_CHANGED, OBJECT_CREATED, TriggerKind, UNAVAILABLE_FEATURE_TOOLTIP} from '../triggerConstants';
import SelectableRadioCard from './SelectableRadioCard';
import {EventTypeCapabilities} from './types';

const useStyles = createStyles((theme) => ({
  // The MultiSelect's selected-value pills default to a small font regardless of the
  // input `size`, so bump the pill label to match the rest of the form's inputs.
  valuePill: {
    fontSize: theme.fontSizes.md,
    height: 'auto',
  },
}));

interface EventTypeSectionProps {
  capabilities: EventTypeCapabilities;
  commonNounLabel: string;
  config: Partial<EventTriggerConfig>;
  fieldOptions: Array<{value: string; label: string}>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  nestedEventInputContainerClassName: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onTriggerKindChange: (nextTriggerKind: TriggerKind) => void;
  sectionClassName: string;
  selectDropdownClassName: string;
  triggerKind: TriggerKind | undefined;
  valuesData: Array<{value: string; label: string}>;
}

export default function EventTypeSection({
  capabilities,
  commonNounLabel,
  config,
  fieldOptions,
  getCardClassName,
  nestedEventInputContainerClassName,
  onChange,
  onTriggerKindChange,
  sectionClassName,
  selectDropdownClassName,
  triggerKind,
  valuesData,
}: EventTypeSectionProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <Stack spacing={4} className={sectionClassName}>
      <RoutineFieldHeader title="What specific event should trigger this agent to run?" />
      <Radio.Group value={triggerKind ?? ''}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value={OBJECT_CREATED}
            label={`When the ${commonNounLabel} is created`}
            disabled={capabilities.objectCreatedDisabled}
            showTooltip={capabilities.showObjectCreatedUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onTriggerKindChange(OBJECT_CREATED);
            }}
            cardClassName={getCardClassName(
              triggerKind === OBJECT_CREATED,
              capabilities.objectCreatedDisabled
            )}
          />
          <SelectableRadioCard
            value={FIELD_CHANGED}
            label={`A change happens to ${withIndefiniteArticle(commonNounLabel)}`}
            disabled={capabilities.fieldChangesDisabled}
            showTooltip={capabilities.showFieldChangesUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onTriggerKindChange(FIELD_CHANGED);
            }}
            cardClassName={getCardClassName(triggerKind === FIELD_CHANGED, capabilities.fieldChangesDisabled)}
          >
            {triggerKind === FIELD_CHANGED && (
              <Box className={nestedEventInputContainerClassName}>
                <Stack spacing="md">
                  <Select
                    label="When this..."
                    placeholder="Select field"
                    value={config.field ?? ''}
                    onChange={(value) => {
                      onChange({
                        field: value ?? '',
                        to_values: [],
                      });
                    }}
                    data={fieldOptions}
                    classNames={{dropdown: selectDropdownClassName}}
                    size="md"
                    disabled={!capabilities.canConfigureFieldChanges}
                  />
                  <MultiSelect
                    label="changes to..."
                    placeholder="Select values"
                    value={config.to_values ?? []}
                    onChange={(values) => {
                      onChange({to_values: values});
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                      }
                    }}
                    data={valuesData}
                    classNames={{dropdown: selectDropdownClassName, value: classes.valuePill}}
                    size="md"
                    disabled={config.field == null || config.field === ''}
                  />
                </Stack>
              </Box>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
